import { FC, useId, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import styles from './vintageProfile.module.sass';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';
import { int } from "@/lib/format";

export interface VintageRow {
  /** Origination period label, e.g. "2024-03" */
  month: string;
  /** Originated amount (Y) */
  amount: number;
  /** Optional loan count (shown in tooltip) */
  count?: number;
  /** Same-month-prior-year amount, drawn behind the main bar as a translucent
   *  ghost so the YoY delta is visible without leaving the chart. Optional —
   *  omit (or set to null) for months with no prior-year comparable. */
  yoyAmount?: number | null;
}

export interface VintageProfileProps {
  rows: VintageRow[];
  /** Show a cumulative-originated line overlay (default true) */
  showCumulative?: boolean;
  /** Currency code for formatting (default "USD") */
  currency?: string;
  /** Chart height (default 240) */
  height?: number;
  /** Render an auto-callout pointing at the peak month bar (highest amount). */
  highlightPeak?: boolean;
  /** Render an auto-callout pointing at the trough month bar (lowest amount).
   *  Trough excludes zero-amount months (they're noise, not a story). */
  highlightTrough?: boolean;
  /** External hover index — when set, overrides the chart's internal hover.
   *  Use with `onHoverChange` to sync hover across multiple charts. */
  externalHoverIdx?: number | null;
  /** Notify parent of internal hover changes (for cross-chart sync). */
  onHoverChange?: (idx: number | null) => void;
  /** Click handler on a bar — fires with the clicked row. Use for drill-down
   *  (e.g. open the clicked vintage cohort in another tab). When set, bars
   *  show a pointer cursor. */
  onBarClick?: (row: VintageRow, idx: number) => void;
  className?: string;
}

const niceTicks = (max: number, count = 5): number[] => {
  if (max <= 0) return [0];
  const raw = max / (count - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  let step: number;
  if (norm <= 1.5) step = mag;
  else if (norm <= 3) step = 2 * mag;
  else if (norm <= 7) step = 5 * mag;
  else step = 10 * mag;
  const ticks: number[] = [];
  for (let t = 0; t <= max + step * 0.01; t += step) ticks.push(Math.round(t * 1e6) / 1e6);
  if (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step);
  return ticks;
};

const fmtAbbrev = (v: number): string => {
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return v.toFixed(0);
};

const fmtMoney = (v: number, ccy: string): string => `${ccy} ${fmtAbbrev(v)}`;

/**
 * VintageProfile — Glasstape DS premium origination-timeline chart.
 *
 * Visualizes outstanding originated per month with optional cumulative overlay.
 *
 * Audit checklist:
 *   ✓ Only --gt-* tokens
 *   ✓ Single accent color across all bars (height = amount, not category)
 *   ✓ 1px hairline grid + 10px mono axis labels
 *   ✓ X-ticks at intervals to avoid label overlap (max 8 labels)
 *   ✓ Hover crosshair + active-bar highlight + dark-pill tooltip
 *   ✓ Cumulative overlay = dashed --gt-text-muted (subtle reference)
 *   ✓ No static inline styles (only dynamic SVG coords)
 *   ✓ Light/dark via --gt-* cascade
 */
export const VintageProfile: FC<VintageProfileProps> = ({
  rows,
  showCumulative = true,
  currency = 'USD',
  height = 240,
  highlightPeak = false,
  highlightTrough = false,
  externalHoverIdx,
  onHoverChange,
  onBarClick,
  className,
}) => {
  const uid = useId().replace(/:/g, '');
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [W, setW] = useState(600);
  const [internalHIdx, setInternalHIdx] = useState<number | null>(null);
  const hIdx = externalHoverIdx !== undefined ? externalHoverIdx : internalHIdx;
  const setHIdx = useCallback((idx: number | null) => {
    setInternalHIdx(idx);
    onHoverChange?.(idx);
  }, [onHoverChange]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = Math.round(el.clientWidth);
      if (w > 0) setW(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const H = height;
  const pad = { top: 14, right: 16, bottom: 28, left: 48 };
  const iw = Math.max(10, W - pad.left - pad.right);
  const ih = H - pad.top - pad.bottom;

  const data = useMemo(() => rows.filter((r) => Number.isFinite(r.amount)), [rows]);

  const cumulative = useMemo(() => {
    let acc = 0;
    return data.map((r) => {
      acc += r.amount;
      return acc;
    });
  }, [data]);

  const maxAmount = useMemo(() => Math.max(...data.map((r) => r.amount), 0.01), [data]);
  const maxCum = useMemo(() => cumulative[cumulative.length - 1] ?? maxAmount, [cumulative, maxAmount]);

  const ticks = useMemo(() => niceTicks(maxAmount, 5), [maxAmount]);
  const yMax = ticks[ticks.length - 1] || maxAmount;

  const slot = data.length > 0 ? iw / data.length : iw;
  const barW = Math.max(2, slot * 0.62);

  const xCenter = useCallback((i: number) => pad.left + slot * (i + 0.5), [pad.left, slot]);
  const yBar = useCallback((amt: number) => pad.top + ih - (amt / yMax) * ih, [pad.top, ih, yMax]);
  const yCum = useCallback((c: number) => pad.top + ih - (c / (maxCum || 1)) * ih, [pad.top, ih, maxCum]);

  // Identify peak / trough indices for the optional spotlight callouts.
  // Trough ignores zero-volume months — they're informational gaps, not lows.
  const peakIdx = useMemo(() => {
    if (!highlightPeak || data.length === 0) return -1;
    let best = 0;
    for (let i = 1; i < data.length; i += 1) if (data[i].amount > data[best].amount) best = i;
    return best;
  }, [data, highlightPeak]);
  const troughIdx = useMemo(() => {
    if (!highlightTrough || data.length === 0) return -1;
    let best = -1;
    for (let i = 0; i < data.length; i += 1) {
      if (data[i].amount <= 0) continue;
      if (best === -1 || data[i].amount < data[best].amount) best = i;
    }
    return best;
  }, [data, highlightTrough]);

  // X-tick selection — ~6-8 labels max
  const xTickIdxs = useMemo(() => {
    if (data.length === 0) return [] as number[];
    const target = 8;
    const stride = Math.max(1, Math.ceil(data.length / target));
    const idxs: number[] = [];
    for (let i = 0; i < data.length; i += stride) idxs.push(i);
    if (idxs[idxs.length - 1] !== data.length - 1) idxs.push(data.length - 1);
    return idxs;
  }, [data.length]);

  const onMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current;
      if (!svg || data.length === 0) return;
      const rect = svg.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const idx = Math.max(0, Math.min(data.length - 1, Math.floor((mx - pad.left) / slot)));
      setHIdx(idx);
    },
    [data.length, slot, pad.left],
  );

  const onLeave = useCallback(() => setHIdx(null), []);

  if (W <= 0) {
    return <div ref={wrapRef} className={classNames('gt-scope', styles.wrap, className)} />;
  }
  if (data.length === 0) {
    return <div ref={wrapRef} className={classNames('gt-scope', styles.wrap, className)} />;
  }

  const cumPath = data
    .map((_, i) => `${i === 0 ? 'M' : 'L'}${xCenter(i).toFixed(2)},${yCum(cumulative[i]).toFixed(2)}`)
    .join(' ');

  return (
    <div ref={wrapRef} className={classNames('gt-scope', styles.wrap, className)}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width={W} height={H} className={styles.svg}>
        <defs>
          <clipPath id={`${uid}-clip`}>
            <rect x={pad.left} y={pad.top} width={iw} height={ih} />
          </clipPath>
        </defs>

        {/* Y grid + labels */}
        {ticks.map((t) => (
          <g key={`y-${t}`}>
            <line
              x1={pad.left}
              x2={W - pad.right}
              y1={yBar(t)}
              y2={yBar(t)}
              className={styles.grid}
            />
            <text
              x={pad.left - 8}
              y={yBar(t) + 3.5}
              textAnchor="end"
              className={styles.axisLabel}
            >
              {fmtAbbrev(t)}
            </text>
          </g>
        ))}

        {/* X labels */}
        {xTickIdxs.map((i) => (
          <text
            key={`x-${i}`}
            x={xCenter(i)}
            y={H - 10}
            textAnchor="middle"
            className={styles.axisLabel}
          >
            {data[i].month}
          </text>
        ))}

        {/* YoY ghost bars, translucent, slightly narrower than the main bar,
            sit behind it to reveal the prior-year comparison at a glance. */}
        <g clipPath={`url(#${uid}-clip)`}>
          {data.map((r, i) => {
            const yoy = r.yoyAmount;
            if (yoy == null || !Number.isFinite(yoy) || yoy <= 0) return null;
            const ghostW = barW * 0.75;
            const x = xCenter(i) - ghostW / 2;
            const y = yBar(Math.min(yoy, yMax));
            const h = Math.max(1, pad.top + ih - y);
            return (
              <rect
                key={`${r.month}-yoy`}
                x={x}
                y={y}
                width={ghostW}
                height={h}
                rx={Math.min(3, ghostW / 2)}
                className={styles.yoyGhost}
              />
            );
          })}
        </g>

        {/* Bars */}
        <g clipPath={`url(#${uid}-clip)`}>
          {data.map((r, i) => {
            const x = xCenter(i) - barW / 2;
            const y = yBar(r.amount);
            const h = Math.max(1, pad.top + ih - y);
            const isActive = hIdx === i;
            return (
              <rect
                key={r.month}
                x={x}
                y={y}
                width={barW}
                height={h}
                rx={Math.min(3, barW / 2)}
                className={classNames(styles.bar, isActive && styles.active, hIdx !== null && !isActive && styles.dim)}
              />
            );
          })}
        </g>

        {/* Spotlight annotations, peak / trough callouts. Pure decoration,
            placed in the upper inset so they don't collide with the bars or
            the cumulative line. */}
        {peakIdx >= 0 && (
          <g className={styles.spotlight}>
            <line
              x1={xCenter(peakIdx)}
              x2={xCenter(peakIdx)}
              y1={yBar(data[peakIdx].amount)}
              y2={yBar(data[peakIdx].amount) - 14}
              className={styles.spotlightLine}
            />
            <text
              x={xCenter(peakIdx)}
              y={Math.max(10, yBar(data[peakIdx].amount) - 17)}
              textAnchor="middle"
              className={styles.spotlightLabel}
            >
              Peak, {fmtMoney(data[peakIdx].amount, currency)}
            </text>
          </g>
        )}
        {troughIdx >= 0 && troughIdx !== peakIdx && (
          <g className={styles.spotlight}>
            <line
              x1={xCenter(troughIdx)}
              x2={xCenter(troughIdx)}
              y1={yBar(data[troughIdx].amount)}
              y2={yBar(data[troughIdx].amount) - 14}
              className={styles.spotlightLine}
            />
            <text
              x={xCenter(troughIdx)}
              y={Math.max(10, yBar(data[troughIdx].amount) - 17)}
              textAnchor="middle"
              className={styles.spotlightLabel}
            >
              Low, {fmtMoney(data[troughIdx].amount, currency)}
            </text>
          </g>
        )}

        {/* Cumulative line overlay */}
        {showCumulative && data.length > 1 && (
          <g clipPath={`url(#${uid}-clip)`}>
            <path d={cumPath} className={styles.cumLine} />
            {hIdx !== null && (
              <circle
                cx={xCenter(hIdx)}
                cy={yCum(cumulative[hIdx])}
                r="4"
                className={styles.cumDot}
              />
            )}
          </g>
        )}

        {/* Hover crosshair */}
        {hIdx !== null && (
          <line
            x1={xCenter(hIdx)}
            x2={xCenter(hIdx)}
            y1={pad.top}
            y2={pad.top + ih}
            className={styles.crosshair}
          />
        )}

        {/* Hit rect */}
        <rect
          x={pad.left}
          y={pad.top}
          width={iw}
          height={ih}
          className={styles.hitRect}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          onClick={(e) => {
            if (!onBarClick) return;
            const svg = svgRef.current;
            if (!svg) return;
            const rect = svg.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const idx = Math.max(0, Math.min(data.length - 1, Math.floor((mx - pad.left) / slot)));
            onBarClick(data[idx], idx);
          }}
          style={onBarClick ? { cursor: 'pointer' } : undefined}
        />
      </svg>

      {hIdx !== null && (
        <ChartTooltip
          visible
          x={(svgRef.current?.getBoundingClientRect().left ?? 0) + xCenter(hIdx)}
          y={(svgRef.current?.getBoundingClientRect().top ?? 0) + pad.top + 8}
          anchorBottom
          header={data[hIdx].month}
          rows={[
            {
              color: 'var(--gt-seq-b-4)',
              label: 'Originated',
              value: fmtMoney(data[hIdx].amount, currency),
            },
            ...(data[hIdx].count != null
              ? [{
                  color: 'var(--gt-text-muted)',
                  label: 'Loans',
                  value: int(data[hIdx].count!),
                }]
              : []),
            ...(showCumulative
              ? [{
                  color: 'var(--gt-text-muted)',
                  label: 'Cumulative',
                  value: fmtMoney(cumulative[hIdx], currency),
                }]
              : []),
          ]}
        />
      )}
    </div>
  );
};

VintageProfile.displayName = 'VintageProfile';
export default VintageProfile;
